import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { FprTemplatesService } from '../../../../services/fprTemplate.service'
import { Dialog } from '@angular/cdk/dialog'
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { Router, ActivatedRoute } from '@angular/router'
import { DialogService } from '../../../../services/dialog.service'
import { ErrorService } from '../../../../services/error.service'
import { SpinnerService } from '../../../../services/spinner.service'
import { TranslationService } from '../../../../services/translation.service'
import { MatButtonModule } from '@angular/material/button'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { firstValueFrom } from 'rxjs'
import { FprTemplate } from '../../../../types/fprTemplates'
import { ApiError } from '../../../../types/apiError'
import { FPR_SYSTEM_FIELDS } from '../../../../constants/reports'

@Component({
  selector: 'app-create-fpr-template',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInputModule,
    MatButtonModule,
    MatIcon,
  ],
  templateUrl: './create-fpr-template.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './create-fpr-template.component.scss',
})
export class CreateFprTemplateComponent implements OnInit {
  translationService = inject(TranslationService)
  router = inject(Router)
  private fb = inject(FormBuilder)
  private spinnerService = inject(SpinnerService)
  private dialog = inject(Dialog)
  private dialogService = inject(DialogService)
  private errorService = inject(ErrorService)
  private fprTemplateService = inject(FprTemplatesService)
  private route = inject(ActivatedRoute)

  templateForm = this.fb.group({
    name: ['', Validators.required],
    sku: [FPR_SYSTEM_FIELDS['SKU'] as string, Validators.required],
    supplier: [FPR_SYSTEM_FIELDS['SUPPLIER'] as string, Validators.required],

    qtyManufactured: [
      FPR_SYSTEM_FIELDS.QTY_MANUFACTURED_CANADA as string,
      Validators.required,
    ],
    qtyImported: [
      FPR_SYSTEM_FIELDS.QTY_IMPORTED as string,
      Validators.required,
    ],
    qtyPlacedOnMarket: [
      FPR_SYSTEM_FIELDS.QTY_PLACED_ON_MARKET as string,
      Validators.required,
    ],
    qtyWasteGeneratedAtPremises: [
      FPR_SYSTEM_FIELDS.QTY_WASTE_GENERATED_AT_PREMISES as string,
      Validators.required,
    ],
    qtyCollectedAtEndOfLife: [
      FPR_SYSTEM_FIELDS.QTY_COLLECTED_AT_END_OF_LIFE as string,
      Validators.required,
    ],

    qtyDivertedMechanicalRecycling: [
      FPR_SYSTEM_FIELDS.QTY_MECHANICAL_RECYCLING as string,
      Validators.required,
    ],
    qtyDivertedChemicalRecycling: [
      FPR_SYSTEM_FIELDS.QTY_CHEMICAL_RECYCLING as string,
      Validators.required,
    ],
    qtyDivertedProcessingIntoChemicalsFuels: [
      FPR_SYSTEM_FIELDS.QTY_PROCESSING_INTO_CHEMICALS_FUELS as string,
      Validators.required,
    ],
    qtyDivertedComposting: [
      FPR_SYSTEM_FIELDS.QTY_COMPOSTING as string,
      Validators.required,
    ],
    qtyDivertedDirectReuse: [
      FPR_SYSTEM_FIELDS.QTY_DIRECT_REUSE as string,
      Validators.required,
    ],
    qtyDivertedRefurbishment: [
      FPR_SYSTEM_FIELDS.QTY_REFURBISHMENT as string,
      Validators.required,
    ],
    qtyDivertedRemanufacturing: [
      FPR_SYSTEM_FIELDS.QTY_REMANUFACTURING as string,
      Validators.required,
    ],
    qtyDivertedRepair: [
      FPR_SYSTEM_FIELDS.QTY_REPAIR as string,
      Validators.required,
    ],
    qtyDivertedIncinerationIndustrialProcesses: [
      FPR_SYSTEM_FIELDS.QTY_INCINERATION_INDUSTRIAL_PROCESSES as string,
      Validators.required,
    ],
    qtyDivertedIncinerationWithEnergyRecovery: [
      FPR_SYSTEM_FIELDS.QTY_INCINERATION_WITH_ENERGY_RECOVERY as string,
      Validators.required,
    ],

    qtyFinalDisposalIncinerationNoEnergy: [
      FPR_SYSTEM_FIELDS.QTY_INCINERATION_NO_ENERGY as string,
      Validators.required,
    ],
    qtyFinalDisposalLandfill: [
      FPR_SYSTEM_FIELDS.QTY_LANDFILL as string,
      Validators.required,
    ],

    qtyAlberta: [FPR_SYSTEM_FIELDS.QTY_ALBERTA as string, Validators.required],
    qtyBritishColumbia: [
      FPR_SYSTEM_FIELDS.QTY_BRITISH_COLUMBIA as string,
      Validators.required,
    ],
    qtyManitoba: [
      FPR_SYSTEM_FIELDS.QTY_MANITOBA as string,
      Validators.required,
    ],
    qtyNewBrunswick: [
      FPR_SYSTEM_FIELDS.QTY_NEW_BRUNSWICK as string,
      Validators.required,
    ],
    qtyNewfoundlandAndLabrador: [
      FPR_SYSTEM_FIELDS.QTY_NEWFOUNDLAND_AND_LABRADOR as string,
      Validators.required,
    ],
    qtyNorthwestTerritories: [
      FPR_SYSTEM_FIELDS.QTY_NORTHWEST_TERRITORIES as string,
      Validators.required,
    ],
    qtyNovaScotia: [
      FPR_SYSTEM_FIELDS.QTY_NOVA_SCOTIA as string,
      Validators.required,
    ],
    qtyNunavut: [FPR_SYSTEM_FIELDS.QTY_NUNAVUT as string, Validators.required],
    qtyOntario: [FPR_SYSTEM_FIELDS.QTY_ONTARIO as string, Validators.required],
    qtyPrinceEdwardIsland: [
      FPR_SYSTEM_FIELDS.QTY_PRINCE_EDWARD_ISLAND as string,
      Validators.required,
    ],
    qtyQuebec: [FPR_SYSTEM_FIELDS.QTY_QUEBEC as string, Validators.required],
    qtySaskatchewan: [
      FPR_SYSTEM_FIELDS.QTY_SASKATCHEWAN as string,
      Validators.required,
    ],
    qtyYukon: [FPR_SYSTEM_FIELDS.QTY_YUKON as string, Validators.required],
  })

  title = this.translationService.getCreateTemplateText()

  isEditMode: boolean = false
  templateId: string | null = null
  templateForEdit?: FprTemplate

  ngOnInit(): void {
    this.route.paramMap.subscribe(async (params) => {
      const templateId = params.get('id')
      this.isEditMode = !!templateId

      if (this.isEditMode) {
        this.title = this.translationService.getEditTemplateText()
        await this.setTemplate(templateId!)
      }
    })
  }

  async setTemplate(templateId: string) {
    try {
      this.templateId = templateId
      this.templateForEdit = await firstValueFrom(
        this.fprTemplateService.getById(this.templateId),
      )

      this.setFields()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  setFields() {
    if (this.templateForEdit) {
      const templateData: Partial<FprTemplate> = {
        ...this.templateForEdit,
      }
      this.templateForm.patchValue(templateData)
    }
  }

  async onSubmit() {
    try {
      this.spinnerService.startSpinner()

      if (this.templateForm.valid) {
        const template = this.templateForm.value as FprTemplate

        let successMsg = this.translationService.getTemplateCreatedText()
        if (!this.isEditMode) {
          await firstValueFrom(this.fprTemplateService.create(template))
        } else {
          if (!this.templateId) {
            return
          }
          await firstValueFrom(
            this.fprTemplateService.edit(this.templateId, template),
          )
          successMsg = this.translationService.getTemplateUpdatedText()
        }

        this.dialogService.showDialog(
          this.dialog,
          this.translationService.getFprTemplatesText(),
          successMsg,
          undefined,
          this.router,
          'settings',
        )
      }

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  onReset() {
    if (!this.isEditMode) {
      this.templateForm.reset()
    } else {
      this.setFields()
    }
  }
}
